import { Box, Button, CircularProgress, Paper, TextField, Typography } from "@mui/material";
import { useEffect, useState, useRef } from "react";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import MDButton from "../../components/MDButton";
import DashboardLayout from "../../examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../examples/Navbars/DashboardNavbar";
import apiService from "../../services/api-service";

const NonConformitaAdd = () => {
	const { id } = useParams();
	const navigate = useNavigate();
	const sigCanvas = useRef(null);

	// Area corrente da localStorage
	const currentAreaId = localStorage.getItem("areaSelected");
	const currentAreaName = localStorage.getItem("areaName");

	const [formData, setFormData] = useState({
		cantiere: currentAreaId ? parseInt(currentAreaId) : null,
		descrizione: "",
	});

	const [loading, setLoading] = useState(false);
	const [fieldErrors, setFieldErrors] = useState({
		cantiere: false,
		descrizione: false,
		firma: false,
	});

	const handleSetNotification = (msg, severityType) => {
		if (severityType === "success") {
			toast.success(msg, { id: "nc-add-toast" });
		} else {
			toast.error(msg, { id: "nc-add-toast" });
		}
	};

	useEffect(() => {
		if (id) {
			fetchNC(id);
		}
	}, [id]);

	const fetchNC = async (ncId) => {
		setLoading(true);
		try {
			const response = await apiService.get(`/non-conformita/${ncId}/`, {}, {});
			if (response) {
				setFormData({
					cantiere: response.cantiere,
					descrizione: response.descrizione,
					firma_apertura: response.firma_apertura,
				});
			}
		} catch (error) {
			handleSetNotification("Errore nel caricamento NC", "error");
		} finally {
			setLoading(false);
		}
	};

	const handleInputChange = (name, value) => {
		setFormData((prev) => ({ ...prev, [name]: value }));
		setFieldErrors((prev) => ({ ...prev, [name]: false }));
	};

	const clearSignature = () => {
		if (sigCanvas.current) {
			sigCanvas.current.clear();
			setFieldErrors((prev) => ({ ...prev, firma: false }));
		}
	};

	const handleSave = async () => {
		// Validazione
		const errors = {
			cantiere: !formData.cantiere,
			descrizione: !formData.descrizione || formData.descrizione.trim() === "",
			firma: id ? false : (!sigCanvas.current || sigCanvas.current.isEmpty()),
		};

		setFieldErrors(errors);

		if (Object.values(errors).some((err) => err)) {
			handleSetNotification("Compilare tutti i campi obbligatori", "error");
			return;
		}

		setLoading(true);

		try {
			if (id) {
				// Update (solo descrizione modificabile se APERTA)
				await apiService.patch(`/non-conformita/${id}/`, {}, { descrizione: formData.descrizione });
				handleSetNotification("Non Conformità aggiornata con successo", "success");
			} else {
				// Create - Converti firma in base64
				const firmaBase64 = sigCanvas.current.toDataURL();

				const payload = {
					cantiere: formData.cantiere,
					descrizione: formData.descrizione,
					firma_apertura: firmaBase64,
				};

				const response = await apiService.post("/non-conformita/", {}, payload);
				handleSetNotification(
					`Non Conformità ${response.numero} creata con successo`,
					"success"
				);
			}

			// Redirect alla dashboard
			setTimeout(() => {
				navigate("/quality/non-conformita");
			}, 1500);
		} catch (error) {
			console.error("Errore salvataggio NC:", error);
			handleSetNotification(
				error.response?.data?.error || "Errore durante il salvataggio",
				"error"
			);
		} finally {
			setLoading(false);
		}
	};

	if (loading && id) {
		return (
			<DashboardLayout>
				<DashboardNavbar />
				<Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
					<CircularProgress />
				</Box>
			</DashboardLayout>
		);
	}

	return (
		<DashboardLayout>
			<DashboardNavbar />
			<Box p={3}>
				<Paper elevation={3} sx={{ p: 4, maxWidth: 800, margin: "0 auto" }}>
					<Typography variant="h4" gutterBottom color="primary">
						{id ? "Modifica" : "Nuova"} Non Conformità
					</Typography>

					<Box mt={3} display="flex" flexDirection="column" gap={3}>
						{/* Cantiere */}
						<TextField
							label="Cantiere *"
							value={currentAreaName || ""}
							disabled
							fullWidth
							error={fieldErrors.cantiere}
							helperText={fieldErrors.cantiere ? "Campo obbligatorio" : "Area corrente auto-selezionata"}
						/>

						{/* Descrizione */}
						<TextField
							label="Descrizione Non Conformità *"
							multiline
							rows={6}
							value={formData.descrizione}
							onChange={(e) => handleInputChange("descrizione", e.target.value)}
							error={fieldErrors.descrizione}
							helperText={
								fieldErrors.descrizione
									? "Campo obbligatorio"
									: "Descrizione dettagliata della non conformità rilevata"
							}
							fullWidth
						/>

						{/* Mostra firma esistente in modalità visualizza/edit */}
						{id && formData.firma_apertura && (
							<Box>
								<Typography variant="subtitle1" gutterBottom>
									Firma Apertura
								</Typography>
								<Box
									border="1px solid #ccc"
									borderRadius={2}
									p={1}
									bgcolor="#f9f9f9"
									display="flex"
									justifyContent="center"
								>
									<img
										src={formData.firma_apertura}
										alt="Firma digitale"
										style={{ maxWidth: "100%", height: "auto" }}
									/>
								</Box>
							</Box>
						)}

						{/* Firma Digitale */}
						{!id && (
							<Box>
								<Typography variant="subtitle1" gutterBottom>
									Firma Digitale *
								</Typography>
								<Box
									border={fieldErrors.firma ? "2px solid red" : "1px solid #ccc"}
									borderRadius={2}
									p={1}
									bgcolor="#f9f9f9"
								>
									<SignatureCanvas
										ref={sigCanvas}
										canvasProps={{
											width: 700,
											height: 200,
											className: "signature-canvas",
											style: { border: "1px dashed #999" },
										}}
									/>
								</Box>
								<Box mt={1} display="flex" gap={2}>
									<Button variant="outlined" size="small" onClick={clearSignature}>
										Cancella Firma
									</Button>
									{fieldErrors.firma && (
										<Typography variant="caption" color="error">
											Firma obbligatoria
										</Typography>
									)}
								</Box>
							</Box>
						)}

						{/* Actions */}
						<Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
							<Button
								variant="outlined"
								onClick={() => navigate("/quality/non-conformita")}
								disabled={loading}
							>
								Annulla
							</Button>
							<MDButton
								variant="gradient"
								color="info"
								onClick={handleSave}
								disabled={loading}
							>
								{loading ? <CircularProgress size={24} /> : id ? "Aggiorna" : "Crea NC"}
							</MDButton>
						</Box>
					</Box>
				</Paper>
			</Box>
		</DashboardLayout>
	);
};

export default NonConformitaAdd;
